// components/SeatingManager.js
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { db } from '../firebase';
import { doc, setDoc } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';

const toTitleCase = (str) => str.replace(/(^|\s)\S/g, c => c.toUpperCase());

const EMPTY_TABLE = { name: '', seats: 8, babySeats: 0, strollerSpaces: 0, spareSeats: 0 };

function GuestSelect({ guests, onSelect, placeholder }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const inputRef = useRef(null);

  const filtered = guests.filter(g =>
    g.name.toLowerCase().includes(query.toLowerCase())
  );

  const updatePosition = () => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    setDropdownStyle({
      position: 'fixed',
      top: rect.bottom + 2,
      left: rect.left,
      width: rect.width,
      background: '#fff',
      border: '1px solid #dee2e6',
      borderRadius: 6,
      maxHeight: 220,
      overflowY: 'auto',
      zIndex: 9999,
      boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
    });
  };

  const handleFocus = () => {
    updatePosition();
    setOpen(true);
  };

  const select = (g) => {
    onSelect(g);
    setQuery('');
    setOpen(false);
  };

  const showDropdown = open && (filtered.length > 0 || (query && filtered.length === 0));

  return (
    <div style={{ flex: 1 }}>
      <input
        ref={inputRef}
        type="text"
        className="form-control form-control-sm"
        placeholder={placeholder}
        value={query}
        onChange={e => { setQuery(e.target.value); updatePosition(); setOpen(true); }}
        onFocus={handleFocus}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        autoComplete="off"
      />
      {showDropdown && createPortal(
        <div style={dropdownStyle}>
          {filtered.length > 0 ? filtered.map(g => (
            <div
              key={g.id}
              className="px-3 py-2 small"
              style={{ cursor: 'pointer' }}
              onMouseDown={() => select(g)}
              onMouseEnter={e => e.currentTarget.style.background = '#f0fdf4'}
              onMouseLeave={e => e.currentTarget.style.background = ''}
            >
              <span className="fw-semibold">{g.name}</span>
              <span className="text-muted ms-2" style={{ fontSize: '0.7rem' }}>
                ({(g.adults || 1) + (g.children || 0)} {(g.adults || 1) + (g.children || 0) === 1 ? 'person' : 'persons'})
              </span>
            </div>
          )) : (
            <div className="px-3 py-2 small text-muted">No matches</div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

export default function SeatingManager({ user, currentEvent, onEventUpdate }) {
  const { t, i18n } = useTranslation();
  const [tables, setTables] = useState([]);
  const [newTable, setNewTable] = useState(EMPTY_TABLE);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [tableToDelete, setTableToDelete] = useState(null);
  const [showUnassignedPanel, setShowUnassignedPanel] = useState(false);

  const canEdit = () => {
    if (!currentEvent) return false;
    if (!currentEvent.isShared) return true;
    return currentEvent.userRole === 'operator';
  };

  useEffect(() => {
    if (currentEvent) setTables(currentEvent.seating || []);
  }, [currentEvent]);

  const guests = currentEvent?.guests || [];

  // Guests already assigned to any table
  const assignedGuestIds = new Set(tables.flatMap(tb => tb.guestIds || []));

  const unassignedGuests = guests.filter(g => !assignedGuestIds.has(g.id));
  // Only show guests who might still attend (exclude declined)
  const eligibleUnassigned = unassignedGuests.filter(g => g.confirmed !== 'declined');

  // Persons assigned to a table
  const tablePersons = (tb) =>
    (tb.guestIds || []).reduce((sum, gid) => {
      const g = guests.find(x => x.id === gid);
      return sum + (g ? (g.adults || 1) + (g.children || 0) : 0);
    }, 0);

  const saveToFirebase = async (updatedTables) => {
    if (!user || !currentEvent) return;
    setSaving(true);
    try {
      const ownerId = currentEvent.ownerId || user.uid;
      const eventRef = doc(db, 'users', ownerId, 'events', currentEvent.id);
      await setDoc(eventRef, { seating: updatedTables }, { merge: true });
      setTables(updatedTables);
      onEventUpdate({ ...currentEvent, seating: updatedTables });
    } catch (error) {
      alert(`${t('seating.save_error')}\n\n${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const addTable = () => {
    if (!newTable.name.trim()) return;
    const table = {
      id: Date.now(),
      name: newTable.name,
      seats: parseInt(newTable.seats) || 8,
      babySeats: parseInt(newTable.babySeats) || 0,
      strollerSpaces: parseInt(newTable.strollerSpaces) || 0,
      spareSeats: parseInt(newTable.spareSeats) || 0,
      guestIds: [],
    };
    saveToFirebase([table, ...tables]);
    setNewTable(EMPTY_TABLE);
  };

  const startEdit = (table) => {
    setEditingId(table.id);
    setEditData({ name: table.name, seats: table.seats, babySeats: table.babySeats, strollerSpaces: table.strollerSpaces, spareSeats: table.spareSeats || 0 });
  };

  const saveEdit = () => {
    const updated = tables.map(tb =>
      tb.id === editingId
        ? { ...tb, name: editData.name, seats: parseInt(editData.seats) || 8, babySeats: parseInt(editData.babySeats) || 0, strollerSpaces: parseInt(editData.strollerSpaces) || 0, spareSeats: parseInt(editData.spareSeats) || 0 }
        : tb
    );
    saveToFirebase(updated);
    setEditingId(null);
    setEditData({});
  };

  const cancelEdit = () => { setEditingId(null); setEditData({}); };

  const deleteTable = (id) => { setTableToDelete(id); setShowDeleteConfirm(true); };

  const confirmDelete = () => {
    if (tableToDelete) {
      saveToFirebase(tables.filter(tb => tb.id !== tableToDelete));
      setShowDeleteConfirm(false);
      setTableToDelete(null);
    }
  };

  const assignGuest = (tableId, guest) => {
    const updated = tables.map(tb =>
      tb.id === tableId ? { ...tb, guestIds: [...(tb.guestIds || []), guest.id] } : tb
    );
    saveToFirebase(updated);
  };

  const unassignGuest = (tableId, guestId) => {
    const updated = tables.map(tb =>
      tb.id === tableId ? { ...tb, guestIds: (tb.guestIds || []).filter(id => id !== guestId) } : tb
    );
    saveToFirebase(updated);
  };

  const exportToExcel = () => {
    const locale = i18n.language === 'el' ? 'el-GR' : 'en-US';
    const wb = XLSX.utils.book_new();

    // --- Overview sheet ---
    const overviewData = [
      [t('seating.title'), currentEvent.name],
      [t('report.date_label'), new Date().toLocaleString(locale)],
      [''],
      [t('seating.table_name'), t('seating.seats'), t('seating.baby_seats'), t('seating.stroller_spaces'), t('seating.spare_seats'), t('seating.persons_assigned'), t('seating.empty_seats'), t('seating.assigned_count')],
    ];
    tables.forEach(tb => {
      const persons = tablePersons(tb);
      const empty = Math.max(0, tb.seats - persons - (tb.spareSeats || 0));
      overviewData.push([
        tb.name,
        tb.seats,
        tb.babySeats,
        tb.strollerSpaces,
        tb.spareSeats || 0,
        persons,
        empty,
        (tb.guestIds || []).length,
      ]);
    });
    const totalSeats = tables.reduce((s, tb) => s + tb.seats, 0);
    const totalPersonsSeated = tables.reduce((s, tb) => s + tablePersons(tb), 0);
    overviewData.push(['']);
    overviewData.push([t('report.total'), totalSeats, '', '', totalPersonsSeated, Math.max(0, totalSeats - totalPersonsSeated), '']);

    const overviewSheet = XLSX.utils.aoa_to_sheet(overviewData);
    XLSX.utils.book_append_sheet(wb, overviewSheet, t('seating.overview'));

    // --- Seating detail sheet ---
    const detailData = [[t('seating.table_name'), t('report.guest_name'), t('guests.adults_short'), t('guests.children_short'), t('seating.persons_assigned')]];
    tables.forEach(tb => {
      (tb.guestIds || []).forEach(gid => {
        const g = guests.find(x => x.id === gid);
        if (g) {
          detailData.push([tb.name, g.name, g.adults || 1, g.children || 0, (g.adults || 1) + (g.children || 0)]);
        }
      });
      if ((tb.guestIds || []).length === 0) {
        detailData.push([tb.name, `(${t('seating.no_guests_assigned')})`, '', '', '']);
      }
    });
    const detailSheet = XLSX.utils.aoa_to_sheet(detailData);
    XLSX.utils.book_append_sheet(wb, detailSheet, t('seating.seating_plan'));

    // --- Unassigned guests sheet ---
    const unassignedData = [[t('report.guest_name'), t('guests.adults_short'), t('guests.children_short'), t('seating.persons_assigned')]];
    unassignedGuests.forEach(g => {
      unassignedData.push([g.name, g.adults || 1, g.children || 0, (g.adults || 1) + (g.children || 0)]);
    });
    if (unassignedGuests.length === 0) unassignedData.push([`(${t('seating.all_assigned')})`, '', '', '']);
    const unassignedSheet = XLSX.utils.aoa_to_sheet(unassignedData);
    XLSX.utils.book_append_sheet(wb, unassignedSheet, t('seating.unassigned_guests'));

    const fileName = `${currentEvent.name.replace(/[/\\:*?"<>|]/g, '_')}_${t('seating.filename_suffix')}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, fileName, { bookType: 'xlsx', type: 'binary' });
  };

  if (!currentEvent) {
    return (
      <div className="card shadow-sm">
        <div className="card-body text-center py-5">
          <div className="fs-1">⚠️</div>
          <h4>{t('seating.no_event_selected')}</h4>
          <p>{t('guests.select_event_instruction')}</p>
        </div>
      </div>
    );
  }

  const totalSeats = tables.reduce((s, tb) => s + tb.seats, 0);
  const totalSeated = tables.reduce((s, tb) => s + tablePersons(tb), 0);
  const totalEmpty = Math.max(0, totalSeats - totalSeated);

  return (
    <div>
      {/* Header */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white fw-bold d-flex justify-content-between align-items-center">
          <span>🪑 {t('seating.title')} — {currentEvent.name}</span>
          {saving && <span className="badge bg-secondary">{t('guests.saving')}</span>}
        </div>
        <div className="card-body">
          {/* Stats bar */}
          <div className="row g-2 mb-4">
            <div className="col-6 col-md-3">
              <div className="bg-primary bg-opacity-10 rounded p-2 text-center">
                <div className="small text-muted">{t('seating.total_tables')}</div>
                <div className="fs-4 fw-bold text-primary">{tables.length}</div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="bg-success bg-opacity-10 rounded p-2 text-center">
                <div className="small text-muted">{t('seating.total_seats')}</div>
                <div className="fs-4 fw-bold text-success">{totalSeats}</div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="bg-info bg-opacity-10 rounded p-2 text-center">
                <div className="small text-muted">{t('seating.persons_seated')}</div>
                <div className="fs-4 fw-bold text-info">{totalSeated}</div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className={`${totalEmpty > 0 ? 'bg-warning' : 'bg-secondary'} bg-opacity-10 rounded p-2 text-center`}>
                <div className="small text-muted">{t('seating.empty_seats')}</div>
                <div className={`fs-4 fw-bold ${totalEmpty > 0 ? 'text-warning' : 'text-secondary'}`}>{totalEmpty}</div>
              </div>
            </div>
          </div>

          {/* Unassigned guests panel */}
          {eligibleUnassigned.length > 0 && (
            <div className="mb-4">
              <div className="alert alert-warning d-flex align-items-center justify-content-between gap-2 py-2 mb-0 flex-wrap">
                <div className="d-flex align-items-center gap-2">
                  <i className="fas fa-user-clock"></i>
                  <span><strong>{eligibleUnassigned.length}</strong> {t('seating.guests_not_seated')}</span>
                </div>
                <button
                  className="btn btn-sm btn-warning rounded-pill"
                  onClick={() => setShowUnassignedPanel(p => !p)}
                >
                  <i className={`fas fa-chevron-${showUnassignedPanel ? 'up' : 'down'} me-1`}></i>
                  {t('seating.show_unassigned')}
                </button>
              </div>
              {showUnassignedPanel && (
                <div className="border border-warning rounded-bottom p-2 bg-white" style={{ borderTop: 'none' }}>
                  {eligibleUnassigned.map(g => (
                    <div key={g.id} className="d-flex justify-content-between align-items-center py-1 border-bottom small">
                      <span className="fw-semibold">
                        {g.name}
                        <span className="text-muted fw-normal ms-2" style={{ fontSize: '0.75rem' }}>
                          {(g.adults || 1) + (g.children || 0)} {t('seating.persons_short')}
                        </span>
                      </span>
                      <span className={`badge ${
                        g.confirmed === 'accepted' ? 'bg-success' :
                        g.confirmed === 'maybe'    ? 'bg-warning text-dark' : 'bg-secondary'
                      }`}>
                        {g.confirmed === 'accepted' ? '✅' : g.confirmed === 'maybe' ? '❓' : '⏳'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Add table form */}
          {canEdit() && (
            <div className="row g-2 mb-4 p-3 bg-light rounded align-items-end">
              <div className="col-12 col-md-4">
                <label className="form-label small text-muted mb-1">🪑 {t('seating.table_name')}</label>
                <input
                  className="form-control"
                  placeholder={t('seating.table_name_placeholder')}
                  value={newTable.name}
                  onChange={e => setNewTable({ ...newTable, name: toTitleCase(e.target.value) })}
                  onKeyPress={e => e.key === 'Enter' && addTable()}
                />
              </div>
              <div className="col-6 col-md-2">
                <label className="form-label small text-muted mb-1">💺 {t('seating.seats')}</label>
                <input className="form-control" type="number" min="1" value={newTable.seats}
                  onChange={e => setNewTable({ ...newTable, seats: e.target.value })} />
              </div>
              <div className="col-6 col-md-2">
                <label className="form-label small text-muted mb-1">👶 {t('seating.baby_seats')}</label>
                <input className="form-control" type="number" min="0" value={newTable.babySeats}
                  onChange={e => setNewTable({ ...newTable, babySeats: e.target.value })} />
              </div>
              <div className="col-6 col-md-2">
                <label className="form-label small text-muted mb-1">🛒 {t('seating.stroller_spaces')}</label>
                <input className="form-control" type="number" min="0" value={newTable.strollerSpaces}
                  onChange={e => setNewTable({ ...newTable, strollerSpaces: e.target.value })} />
              </div>
              <div className="col-6 col-md-2">
                <label className="form-label small text-muted mb-1">➕ {t('seating.spare_seats')}</label>
                <input className="form-control" type="number" min="0" value={newTable.spareSeats}
                  onChange={e => setNewTable({ ...newTable, spareSeats: e.target.value })} />
              </div>
              <div className="col-12 col-md-2">
                <button className="btn btn-primary w-100" onClick={addTable} disabled={saving}>
                  <i className="fas fa-plus me-1"></i>{t('seating.add_table')}
                </button>
              </div>
            </div>
          )}

          {/* Tables grid */}
          {tables.length === 0 ? (
            <div className="text-center text-muted py-5">
              <div className="fs-1">🪑</div>
              <p>{t('seating.no_tables')}</p>
            </div>
          ) : (
            <div className="row g-3">
              {tables.map(tb => {
                const persons = tablePersons(tb);
                const spare = tb.spareSeats || 0;
                const occupied = persons + spare;
                const empty = Math.max(0, tb.seats - occupied);
                const pct = tb.seats > 0 ? Math.round((occupied / tb.seats) * 100) : 0;
                const overseated = occupied > tb.seats;
                const full = occupied === tb.seats && tb.seats > 0;
                // header bg: red=overseated, green=full, yellow=partial/empty
                const headerBg = overseated ? '#fef2f2' : full ? '#f0fdf4' : '#fffbeb';
                const barClass  = overseated ? 'bg-danger'  : full ? 'bg-success' : 'bg-warning';
                const isEditing = editingId === tb.id;
                const tableGuests = (tb.guestIds || []).map(gid => guests.find(g => g.id === gid)).filter(Boolean);
                const availableToAssign = guests.filter(g => !assignedGuestIds.has(g.id));

                return (
                  <div key={tb.id} className="col-12 col-md-6 col-lg-4">
                    <div className="card border shadow-sm h-100">
                      {/* Table header */}
                      <div className="card-header d-flex justify-content-between align-items-center py-2"
                        style={{ background: headerBg }}>
                        {isEditing ? (
                          <input
                            className="form-control form-control-sm me-2"
                            value={editData.name}
                            onChange={e => setEditData({ ...editData, name: toTitleCase(e.target.value) })}
                            style={{ maxWidth: 160 }}
                          />
                        ) : (
                          <span className="fw-bold">{tb.name}</span>
                        )}
                        <div className="d-flex gap-1">
                          {isEditing ? (
                            <>
                              <button className="btn btn-sm btn-success" style={{ padding: '0.15rem 0.4rem' }} onClick={saveEdit} disabled={saving}>
                                <i className="fas fa-check" style={{ fontSize: '0.75rem' }}></i>
                              </button>
                              <button className="btn btn-sm btn-outline-secondary" style={{ padding: '0.15rem 0.4rem' }} onClick={cancelEdit}>
                                <i className="fas fa-times" style={{ fontSize: '0.75rem' }}></i>
                              </button>
                            </>
                          ) : canEdit() && (
                            <>
                              <button className="btn btn-sm btn-outline-primary" style={{ padding: '0.15rem 0.4rem' }} onClick={() => startEdit(tb)}>
                                <i className="fas fa-edit" style={{ fontSize: '0.75rem' }}></i>
                              </button>
                              <button className="btn btn-sm btn-outline-danger" style={{ padding: '0.15rem 0.4rem' }} onClick={() => deleteTable(tb.id)}>
                                <i className="fas fa-trash" style={{ fontSize: '0.75rem' }}></i>
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="card-body p-3">
                        {/* Seat stats */}
                        {isEditing ? (
                          <div className="row g-2 mb-3">
                            <div className="col-6">
                              <label className="form-label small text-muted mb-1">💺 {t('seating.seats')}</label>
                              <input className="form-control form-control-sm" type="number" min="1"
                                value={editData.seats} onChange={e => setEditData({ ...editData, seats: e.target.value })} />
                            </div>
                            <div className="col-6">
                              <label className="form-label small text-muted mb-1">👶 {t('seating.baby_seats')}</label>
                              <input className="form-control form-control-sm" type="number" min="0"
                                value={editData.babySeats} onChange={e => setEditData({ ...editData, babySeats: e.target.value })} />
                            </div>
                            <div className="col-6">
                              <label className="form-label small text-muted mb-1">🛒 {t('seating.stroller_spaces')}</label>
                              <input className="form-control form-control-sm" type="number" min="0"
                                value={editData.strollerSpaces} onChange={e => setEditData({ ...editData, strollerSpaces: e.target.value })} />
                            </div>
                            <div className="col-6">
                              <label className="form-label small text-muted mb-1">➕ {t('seating.spare_seats')}</label>
                              <input className="form-control form-control-sm" type="number" min="0"
                                value={editData.spareSeats} onChange={e => setEditData({ ...editData, spareSeats: e.target.value })} />
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="d-flex justify-content-between align-items-center small text-muted mb-1">
                              <span>💺 {persons}/{tb.seats} {t('seating.seats_used')}</span>
                              <span>{pct}%</span>
                            </div>
                            <div className="progress mb-2" style={{ height: 6 }}>
                              <div className={`progress-bar ${barClass}`} style={{ width: `${pct}%` }} />
                            </div>
                            <div className="d-flex flex-wrap gap-2 small mb-3">
                              {empty > 0 && <span className="text-muted">🪑 {empty} {t('seating.empty_seats')}</span>}
                              {tb.babySeats > 0 && <span className="text-info">👶 {tb.babySeats} {t('seating.baby_seats')}</span>}
                              {tb.strollerSpaces > 0 && <span className="text-secondary">🛒 {tb.strollerSpaces} {t('seating.stroller_spaces')}</span>}
                              {spare > 0 && <span className="text-primary">➕ {spare} {t('seating.spare_seats')}</span>}
                            </div>
                          </>
                        )}

                        {/* Assigned guests list */}
                        <div className="mb-2">
                          {tableGuests.map(g => (
                            <div key={g.id} className="d-flex justify-content-between align-items-center py-1 border-bottom">
                              <span className="small">
                                <i className="fas fa-user me-1 text-muted" style={{ fontSize: '0.7rem' }}></i>
                                {g.name}
                                <span className="text-muted ms-1" style={{ fontSize: '0.7rem' }}>
                                  ({(g.adults || 1) + (g.children || 0)} {t('seating.persons_short')})
                                </span>
                              </span>
                              {canEdit() && (
                                <button className="btn btn-sm btn-outline-danger border-0" style={{ padding: '0 0.3rem' }}
                                  onClick={() => unassignGuest(tb.id, g.id)}>
                                  <i className="fas fa-times" style={{ fontSize: '0.7rem' }}></i>
                                </button>
                              )}
                            </div>
                          ))}
                          {tableGuests.length === 0 && (
                            <div className="small text-muted text-center py-2">{t('seating.no_guests_assigned')}</div>
                          )}
                        </div>

                        {/* Assign guest — searchable */}
                        {canEdit() && availableToAssign.length > 0 && (
                          <div className="d-flex gap-1 mt-2">
                            <GuestSelect
                              guests={availableToAssign}
                              placeholder={t('seating.select_guest')}
                              onSelect={g => assignGuest(tb.id, g)}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Export button */}
          {tables.length > 0 && (
            <div className="text-center mt-4">
              <button className="btn btn-success btn-lg rounded-pill px-4" onClick={exportToExcel}>
                <i className="fas fa-file-excel me-2"></i>{t('seating.export')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(5px)' }}
          onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: 20 }}>
              <div className="modal-header border-0 pb-0">
                <div className="text-center w-100">
                  <div className="fs-1 text-danger mb-2"><i className="fas fa-trash-alt"></i></div>
                  <h4 className="modal-title fw-bold text-danger">{t('seating.delete_table_title')}</h4>
                </div>
                <button type="button" className="btn-close" onClick={() => setShowDeleteConfirm(false)}></button>
              </div>
              <div className="modal-body pt-3 text-center">
                <p className="text-muted mb-0">{t('seating.delete_table_confirm')}</p>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setShowDeleteConfirm(false)}>
                  <i className="fas fa-times me-1"></i>{t('common.cancel')}
                </button>
                <button className="btn btn-danger rounded-pill px-4" onClick={confirmDelete} disabled={saving}>
                  <i className="fas fa-trash-alt me-1"></i>{saving ? t('guests.deleting') : t('common.delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
